import React, { FC, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import Modal from '../common/Modal'
import { cloneDeep, concat, filter, pull, sortBy, includes, orderBy } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { compile } from '../../../utils/search'

interface Member {
  code: string | null
  member_id: number
  name: string
  kana: string | null
  email: string
  image_url: string | null
}

interface Props {
  isDisplay: boolean
  closeCallback: VoidFunction
  addCallback: (memberIds: number[]) => void
  closeable?: boolean
}

const AddExcludeMemberModal: FC<Props> = (props) => {
  const {
    task,
  } = useSelector((state: RootState) => state)

  const {
    isDisplay,
    closeCallback,
    addCallback,
    closeable,
  } = props

  const [selectMemberIds, setSelectMemberIds] = useState<number[]>([])
  const [joinedMemberIds, setJoinedMemberIds] = useState<number[]>([])

  const [dataList, setDataList] = useState<(Member | null)[]>()
  const [dataListInit, setDataListInit] = useState<(Member | null)[]>()
  const [searchWord, setSearchWord] = useState<string>("")

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setDataList(cloneDeep(dataListInit))
      return
    }
    const word = words.split(/\s+/)
    const list = filter(dataListInit, (member) => {
      if (member === null) return false
      return compile(word).test(member.name + member.email + member.code) || includes(selectMemberIds, member.member_id)
    }) as Member[]
    setDataList(sortBy(list, 'id'))
  }

  const reset = () => {
    setSearchWord("")
    setDataList(cloneDeep(dataListInit))
  }

  const closeModal = () => {
    closeCallback()
    reset()
    setSelectMemberIds([])
  }

  const submit = () => {
    addCallback(selectMemberIds)
    closeModal()
  }

  const clickCheckbox = (memberId: number) => {
    if (!memberId || includes(joinedMemberIds, memberId)) return
    if (includes(selectMemberIds, memberId)) {
      setSelectMemberIds(pull(selectMemberIds, memberId))
    } else {
      setSelectMemberIds(concat(selectMemberIds, memberId))
    }
    search(searchWord)
  }

  useEffect(() => {
    if (!task.taskExcludeGenerateMemberSetting) return
    setDataList(orderBy(task.taskExcludeGenerateMemberSetting.full_members, ['id'], ['asc']))
    setDataListInit(orderBy(task.taskExcludeGenerateMemberSetting.full_members, ['id'], ['asc']))
    setJoinedMemberIds(task.taskExcludeGenerateMemberSetting.exclude_members.map((v) => v.member_id))
  }, [task.taskExcludeGenerateMemberSetting])

  return (
    <Modal
      title="作業データを自動生成しないメンバーを追加する"
      show={isDisplay}
      wrapperClass="setting_admin"
      closeCallback={closeModal}
      closeable={closeable}
    >
      <>
        <div className="item">
          <div className="modal-keyword-search">
            <div className="title">
              作業データを自動生成しないメンバーを追加する
            </div>
            <div
              className="search_box"
              style={{
                position: "relative",
                top: 0,
                border: "none",
                boxShadow: "none",
                padding: 0,
              }}
            >
              <input
                type="text"
                placeholder="キーワード検索"
                onChange={(event) => search(event.target.value)}
                value={searchWord}
              />
              {
                searchWord && (
                  <button type="button" className="reset" onClick={reset}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
          </div>
          <div className="detail row_1">
            <table className="modal-flex-table admin">
              <thead>
                <tr>
                  <th>追加</th>
                  <th>名前</th>
                  <th>メールアドレス</th>
                </tr>
              </thead>
              <tbody>
                {
                  dataList && dataList.map((member: Member) => (
                    <tr
                      key={member.member_id}
                      className={includes(joinedMemberIds, member.member_id) ? "disable" : ""}
                      onClick={() => clickCheckbox(member.member_id)}
                    >
                      <td className="modal-table-checkbox">
                        <input
                          name={`member_id_${member.member_id}`}
                          id={`member_id_${member.member_id}`}
                          value={member.member_id}
                          checked={includes(selectMemberIds, member.member_id)}
                          type="checkbox"
                          disabled={includes(joinedMemberIds, member.member_id)}
                          readOnly
                        />
                      </td>
                      <td className="modal-table-name">
                        {member.name}
                        {
                          !!member.code && (
                            <>
                              {`(${member.code})`}
                            </>
                          )
                        }
                      </td>
                      <td className="modal-table-mail">
                        {member.email}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </>
      <>
        <button type="button" className="button cancel" onClick={closeModal} disabled={!closeable}>
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={submit}
          disabled={!selectMemberIds.length}
        >
          追加
        </button>
      </>
    </Modal>
  )
}

AddExcludeMemberModal.defaultProps = {
  closeable: undefined,
}

export default AddExcludeMemberModal
